import { useFormContext } from 'react-hook-form';
import { cn } from '../../../../lib/utils';
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../atoms';
import { MultiSelect } from '../multi-select';

// ----------------------------------------------------------------------

type RHFSelectProps = {
  name: string;
  maxHeight?: boolean | number;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  options: {
    label: string;
    value: string;
  }[];
  [key: string]: any;
};

const RHFSelect = ({
  name,
  disabled = false,
  options = [],
  className = '',
  placeholder = 'Select'
}: RHFSelectProps) => {
  const { control, setValue } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const valueSelected = options
          .map(opt => opt.value)
          .includes(field.value);

        return (
          <FormItem>
            <Select
              disabled={disabled}
              onValueChange={value => {
                field.onChange(value);
                setValue(name, value, { shouldValidate: true });
              }}
              value={field.value}
            >
              <SelectTrigger
                className={cn(
                  className,
                  error?.message && 'border-destructive text-destructive',
                  !field.value && !error?.message && 'text-muted-foreground'
                )}
              >
                {valueSelected ? (
                  <SelectValue>
                    {options.find(opt => opt.value === field.value)?.label}
                  </SelectValue>
                ) : (
                  placeholder
                )}
              </SelectTrigger>
              <SelectContent>
                {options.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

// ----------------------------------------------------------------------

type RHFMultiSelectProps = {
  name: string;
  label?: string;
  chip?: boolean;
  checkbox?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode;
  className?: string;
  options: {
    label: string;
    value: string;
  }[];
};

export const RHFMultiSelect = ({
  name,
  label,
  options,
  placeholder,
  helperText,
  className,
  ...other
}: RHFMultiSelectProps) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className={className}>
          {label && <FormLabel>{label}</FormLabel>}
          <MultiSelect
            {...field}
            {...other}
            options={options}
            placeholder={placeholder}
            className={cn(
              error?.message && 'border-destructive text-destructive'
            )}
            onValueChange={field.onChange}
            defaultValue={field.value}
          />
          {(error || helperText) && (
            <FormMessage>{error ? error.message : helperText}</FormMessage>
          )}
        </FormItem>
      )}
    />
  );
};

export default RHFSelect;
